import ArrowDownButtonEndIcon from '@assets/jsx-icon/ArrowDownButtonEndIcon';
import ArrowDownIcon from '@assets/jsx-icon/ArrowDownIcon';
import { appConfig } from '@configs/index';
import { ContraventionStatus, OutCome, PCNDuplicate, PotentialContraventionWithInfo } from '@EcamModel/model';
import { Box, Button, Collapse, Divider, IconButton, Stack, Typography } from '@mui/material';
import color from '@theme/Colors';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from 'src/helpers';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

type Props = {
    reason?: string;
    cancellationNotes?: string;
    pcnDuplicatesWarning: PCNDuplicate[];
    currentStep: number;
    pcnGroupingDuplicatesWarning: PCNDuplicate[];
    handleOpenPopUp: () => void;
};

export const statusMapping = {
    [ContraventionStatus.Approved]: 'Approved',
    [ContraventionStatus.Cancelled]: 'Cancelled',
    [ContraventionStatus.InProgress]: 'In Progress',
    [ContraventionStatus.Open]: 'Open',
    [ContraventionStatus.Paid]: 'Paid',
    [ContraventionStatus.Paused]: 'Paused',
    [ContraventionStatus.Timeout]: 'Timeout',
    [ContraventionStatus.SystemCancelled]: 'System cancelled',
};

export const outComeStatusMapping = {
    [OutCome.Approved]: ContraventionStatus.Approved,
    [OutCome.Cancelled]: ContraventionStatus.Cancelled,
    [OutCome.SystemCancelled]: ContraventionStatus.Cancelled,
    [OutCome.InProgress]: ContraventionStatus.InProgress,
    [OutCome.Pending]: ContraventionStatus.InProgress,
};

export const isCancelledStatus = (status?: ContraventionStatus) =>
    status === ContraventionStatus.Cancelled || status === ContraventionStatus.SystemCancelled;

export default function DuplicatePCDetail({
    reason,
    cancellationNotes,
    pcnDuplicatesWarning,
    currentStep,
    pcnGroupingDuplicatesWarning,
    handleOpenPopUp,
}: Props) {
    const [collapsePcnDuplicatesWarning, setCollapsePcnDuplicatesWarning] = useState<boolean>(false);

    const groupableItems = pcnGroupingDuplicatesWarning.filter(
        (dup) => dup.status !== ContraventionStatus.SystemCancelled
    );

    const handleCollapsePcnDuplicatesWarning = () => {
        if (pcnDuplicatesWarning?.length > 1) {
            setCollapsePcnDuplicatesWarning(!collapsePcnDuplicatesWarning);
        }
    };

    return (
        <>
            {reason && (
                <Typography variant="body1" color="initial">
                    Reason for cancellation:
                    <Typography pl={'5px'} variant="caption">
                        {reason ?? ''}
                    </Typography>
                </Typography>
            )}

            {cancellationNotes && (
                <Typography>
                    Description:
                    <Typography pl={'5px'} variant="caption">
                        {cancellationNotes ?? ''}
                    </Typography>
                </Typography>
            )}

            {pcnDuplicatesWarning.length > 0 && (
                <Stack direction={'column'} spacing={collapsePcnDuplicatesWarning ? 1 : 0} mb={2}>
                    <Stack
                        direction={'row'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        onClick={handleCollapsePcnDuplicatesWarning}
                        sx={{
                            cursor: pcnDuplicatesWarning?.length > 1 ? 'pointer' : 'default',
                            ':hover .MuiIconButton-root': {
                                bgcolor: '#0000000a',
                            },
                            ':active .MuiIconButton-root': {
                                bgcolor: '#0000001f',
                            },
                        }}
                    >
                        <Typography>
                            A duplicate contravention for the same location, zone, VRN and contravention reason has been
                            create within a 24-hour period. Please review these contraventions
                        </Typography>
                        {pcnDuplicatesWarning?.length > 1 && (
                            <Box flex={1} textAlign={'end'}>
                                <IconButton>
                                    <ArrowDownButtonEndIcon
                                        style={{
                                            transition: 'transform 0.3s ease',
                                            transform: `rotate(${collapsePcnDuplicatesWarning ? -180 : 0}deg)`,
                                        }}
                                    />
                                </IconButton>
                            </Box>
                        )}
                    </Stack>

                    <Collapse in={pcnDuplicatesWarning?.length > 1 ? collapsePcnDuplicatesWarning : true}>
                        {pcnDuplicatesWarning?.map((e, index) => {
                            return (
                                <Stack direction={'column'} spacing={1} mt={1} bgcolor={color.white} borderRadius={1}>
                                    <DuplicatePcnCard
                                        item={e}
                                        detailUrl={
                                            e.source === 'Stella'
                                                ? `${appConfig.apps.stella}/ContraventionManagement/Contravention/Details/${e.id}`
                                                : `/verification/contravention/${e.id}`
                                        }
                                    />
                                </Stack>
                            );
                        })}
                    </Collapse>
                </Stack>
            )}

            {pcnGroupingDuplicatesWarning.length > 0 && currentStep === 1 && (
                <Stack direction={'column'} spacing={1}>
                    {pcnDuplicatesWarning?.length > 0 && collapsePcnDuplicatesWarning && (
                        <Divider
                            orientation="horizontal"
                            flexItem
                            sx={{
                                m: 2,
                                borderColor: '#E6A8B9',
                            }}
                            variant="middle"
                        />
                    )}
                    {groupableItems.length > 0 && (
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                            <Typography variant="h5" color={color.danger}>
                                Group the following duplicate PCNs into a single group?
                            </Typography>
                        </Stack>
                    )}

                    {pcnGroupingDuplicatesWarning?.map((e, index) => {
                        return (
                            <DuplicatePcnCard
                                item={e}
                                detailUrl={
                                    e.source === 'Stella'
                                        ? `${appConfig.apps.stella}/ContraventionManagement/Contravention/Details/${e.id}`
                                        : `/verification/contravention/${e.id}`
                                }
                            />
                        );
                    })}

                    {groupableItems.length > 0 && (
                        <Button variant="contained" sx={{ maxWidth: '160px' }} onClick={handleOpenPopUp}>
                            Confirm grouping
                        </Button>
                    )}
                </Stack>
            )}
        </>
    );
}

export const DuplicatePcnCard: React.FC<{
    detailUrl: string;
    item: PCNDuplicate;
    bgColor?: string;
    isDisplayReferencePopup?: boolean;
}> = ({ item, detailUrl, bgColor, isDisplayReferencePopup = false }) => {
    return (
        <Stack direction="column" spacing={1} bgcolor={bgColor || color.white} p={1} borderRadius={1}>
            {!isDisplayReferencePopup && (
                <Typography minWidth={195} fontWeight={500}>
                    <Link
                        to={detailUrl}
                        target="_blank"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        <OpenInNewIcon
                            sx={{
                                fontSize: 16,
                                color: 'inherit',
                                position: 'relative',
                                top: '-1px',
                            }}
                        />

                        <span>PCN #{item.id ?? ' '}</span>
                    </Link>
                </Typography>
            )}

            <Stack
                direction="row"
                key={item.id}
                divider={
                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                            borderRightWidth: 1,
                            borderColor: color.textPrimary,
                            py: '3px',
                        }}
                    />
                }
                spacing={2}
            >
                {isDisplayReferencePopup && (
                    <Typography>
                        PCN{' '}
                        <Link to={detailUrl} target="_blank">
                            #{item.id ?? ' '}
                        </Link>
                    </Typography>
                )}
                <Typography>Source: {item.source}</Typography>
                <Typography>Status: {statusMapping[item.status]}</Typography>
                {item.step && <Typography>Step: {item.step}</Typography>}
                {item.firstIssuedDate && <Typography>Date: {formatDateTime(item.firstIssuedDate)}</Typography>}
            </Stack>
        </Stack>
    );
};
